/**
 * Hotel PAN Details API
 *
 * Handles PAN add/remove operations via CBackend.
 * CBackend forwards to Agent API with api_call_identifier = HOTEL_PAN_DETAILS.
 *
 * Operations:
 * - hotel_pan_detail_add: Add PAN (10-digit PAN entered and valid)
 * - hotel_pan_detail_remove: Remove PAN (empty panNumber, is_validated=false)
 */
import { HOTELS_ENDPOINTS } from '@/api/endpoints';
import { logger } from '@/shared/utils/logger';
import { generateUUID } from '@/shared/utils/uuid';
import { baseFetch, createApiError, getCBContext } from './baseApi';

// ============================================
// Error Class
// ============================================

export const PanDetailsError = createApiError('PanDetailsError');

// ============================================
// Types
// ============================================

export type PanDetailActionType = 'hotel_pan_detail_add' | 'hotel_pan_detail_remove';

export interface PanDetailsApiResponse {
  type: 'hotels-pan-detail';
  panNumber: string | null;
  isValidated: boolean;
  success: boolean;
  error?: { code: string; message: string };
}

export interface PanDetailsRequestParams {
  sessionId: string;
  userId: string;
  actionType: PanDetailActionType;
  panNumber: string;
  isValidated: boolean;
}

interface RawCBackendResponse {
  success?: boolean;
  panNumber?: string | null;
  isValidated?: boolean;
  errorCode?: string;
  errorMessage?: string;
}

// ============================================
// Helper Functions
// ============================================

function buildCBackendPayload(params: PanDetailsRequestParams): Record<string, unknown> {
  const context = getCBContext();

  return {
    request_id: generateUUID(),
    conversation_id: generateUUID(),
    uuid: context.uuid,
    auth: context.authToken,
    device_info: {
      device_id: context.deviceId,
      device_type: 'DESKTOP',
      app_ver: '1.21.1',
      platform: 'DESKTOP',
    },
    rephrased_query: getQueryForAction(params.actionType, params.panNumber),
    intents: [],
    action_type: params.actionType,
    request_json: {
      panNumber: params.panNumber,
      is_validated: params.isValidated,
    },
  };
}

function getQueryForAction(actionType: PanDetailActionType, panNumber: string): string {
  switch (actionType) {
    case 'hotel_pan_detail_add':
      return `update pan details to ${panNumber}`;
    case 'hotel_pan_detail_remove':
      return 'Remove PAN details';
    default:
      return 'PAN operation';
  }
}

// ============================================
// API Function
// ============================================

/**
 * Call PAN details CBackend API for add/remove operations.
 * POST /hotels/pan-details?sessionId=...&userId=...
 */
export async function callPanDetailsApi(
  params: PanDetailsRequestParams,
): Promise<PanDetailsApiResponse> {
  const emptyResponse: PanDetailsApiResponse = {
    type: 'hotels-pan-detail',
    panNumber: null,
    isValidated: false,
    success: false,
    error: { code: 'UNKNOWN_ERROR', message: 'Unknown error occurred' },
  };

  const payload = buildCBackendPayload(params);

  logger.warn('[PanDetailsApi] Calling CBackend API:', {
    actionType: params.actionType,
    panNumber: params.panNumber ? '***' : '',
  });

  const { data, error } = await baseFetch<RawCBackendResponse>({
    endpoint: HOTELS_ENDPOINTS.PAN_DETAILS,
    queryParams: { sessionId: params.sessionId, userId: params.userId },
    body: payload,
    logTag: 'PanDetailsApi',
  });

  if (error) {
    logger.error('[PanDetailsApi] Network error:', error);
    return { ...emptyResponse, error };
  }

  if (data?.errorCode ?? data?.errorMessage) {
    const apiError = {
      code: data.errorCode ?? 'API_ERROR',
      message: data.errorMessage ?? 'Unknown API error',
    };
    logger.error('[PanDetailsApi] API error:', apiError);
    return { ...emptyResponse, error: apiError };
  }

  if (data?.success === false) {
    const apiError = {
      code: data.errorCode ?? 'OPERATION_FAILED',
      message: data.errorMessage ?? 'PAN operation failed',
    };
    logger.error('[PanDetailsApi] Operation failed:', apiError);
    return { ...emptyResponse, error: apiError };
  }

  const panNumber = data?.panNumber ?? null;
  const isValidated = data?.isValidated ?? false;

  logger.info('[PanDetailsApi] Success:', { panNumber: panNumber ? '***' : '', isValidated });

  return {
    type: 'hotels-pan-detail',
    panNumber,
    isValidated,
    success: true,
  };
}
